import { KeyOfType, ListItem, ValidationFunction } from "../types";
import { Select } from "./select";
import { JSXElement, Show, createContext, useContext } from "solid-js";
import { Required } from "./universal/required-icon";
import { ErrorMessage } from "./universal/validation";
import QuillEditor, { IQuillEditorWrapper } from "./universal/quill-editor";
import { SetStoreFunction, Store } from "solid-js/store";
import ImageCropper, { IImageCropperWrapper } from "./universal/image-cropper";
import { Options } from "./options";

export type TModelContext<T extends object> = [get: Store<T>, set: SetStoreFunction<T>, errors: Record<string, string>, validate: any];
const ModelContext = createContext();
export function useModel() { return useContext(ModelContext); }

interface IModelProvider<T extends object> {
  model: T
  setModel: (key: string, value: any) => void
  validation: {
    validate: any
    errors: Record<string, string>
  }
  children: any
}
export function ModelProvider<T extends object>(props: IModelProvider<T>) {
  return (
    <ModelContext.Provider value={[ props.model, props.setModel, props.validation.errors, props.validation.validate ] as TModelContext<T>}>
      {props.children}
    </ModelContext.Provider>
  );
}

interface IModelSelect<T extends object> {
  field: keyof KeyOfType<T, string>
  list: ListItem[]
  label?:string|(() => JSXElement)
  required?: boolean;
  width?: string;
  nolabel?: boolean;
  validators?: ValidationFunction[];
}
export function ModelSelect<T extends object>(props: IModelSelect<T>) 
{
    const [model, setModel, errors, validate] = useModel() as TModelContext<T>;
    const validators: ValidationFunction[] = [];
    if (props.required) validators.push((el: HTMLInputElement) => (!el.value || el.value == "null") && "Please select a value");
    if (props.validators) validators.push(...props.validators);
    return (
    <>
        <Show when={!props.nolabel}>
            <Show when={props.label}>
                <Show when={props.label instanceof String}>
                    <label>
                        <Show when={props.required}><Required /></Show>
                        {props.label as string}
                    </label>
                </Show>
                <Show when={!(props.label instanceof String)}>
                    <label>
                        <Show when={props.required}><Required /></Show>
                        {(props.label as () => JSXElement)()}
                    </label>
                </Show>
            </Show>
            <Show when={!props.label}>
                <label>
                    <Show when={props.required}><Required /></Show>
                    {props.field}
                </label>
            </Show>
        </Show>
        {/* <Select
           value={model[props.field] as string}
           style={{ width: props.width || "100%" }}
           list={props.list}
           onInput={(value) => setModel(props.field, value)}
        /> */}
        <select
            id={props.field as string}
            name={props.field as string}
            class="form-control"
            use:validate={validators}
            value={model[props.field] as string}
            style={{ width: props.width || "100%" }}
            onInput={(e) => setModel(props.field, e.currentTarget.value as any)}>
                <Options list={props.list} />
            </select>
        <ErrorMessage error={errors[props.field]} />
    </>
    )
}

export interface IModelTextBox<T> {
  field: keyof KeyOfType<T,string|number>;
  label?: string|(() => JSXElement);
  type?: string;
  format?: string;
  required?: boolean;
  validators?: ValidationFunction[];
  placeholder?: string;
  width?: string;
  nolabel?: boolean;
};

const required: ValidationFunction = (el: HTMLInputElement) => !el.value && "Please enter a value";

export function ModelTextBox <T extends object>(props: IModelTextBox<T>) {
    const [model, setModel, errors, validate] = useModel() as TModelContext<T>;
    const validators: ValidationFunction[] = [];
    if (props.required) validators.push(required);
    if (props.validators) validators.push(...props.validators);
    return ( 
    <>
        <Show when={!props.nolabel}>
            <Show when={props.label}>
                <Show when={props.label instanceof String}>
                    <label>
                        <Show when={props.required}><Required /></Show>
                        {props.label as string}
                    </label>
                </Show>
                <Show when={!(props.label instanceof String)}>
                    <label>
                        <Show when={props.required}><Required /></Show>
                        {(props.label as () => JSXElement)()}
                    </label>
                </Show>
            </Show>
            <Show when={!props.label}>
                <label>
                    <Show when={props.required}><Required /></Show>
                    {props.field}
                </label>
            </Show>
        </Show>
        <div style={{ "display": "inline-block", "width": props.width || "100%" }}>
            <div>
                <input
                    id={props.field as string}
                    name={props.field as string}
                    class="form-control"
                    use:validate={validators}
                    value={model[props.field] as string}
                    style="width: 100%"
                    type={props.type || "text"}
                    data-lpignore="true"
                    placeholder={props.placeholder}
                    onInput={(e) => setModel(props.field, e.currentTarget.value as any)}
                />   
            </div>   
            <div>
                <ErrorMessage error={errors[props.field]} />
            </div>  
        </div>
    </>
    )
};

//type TValidator = (el: HTMLInputElement) => boolean|string;
interface IModelDate<T extends object> {
    field: keyof KeyOfType<T,string|number>;
    label: () => JSXElement;
    validators?: ValidationFunction[];
    required?: boolean;
}
export function ModelDate<T extends object> (props: IModelDate<T>) {
    const [model, setModel, errors, validate] = useModel() as TModelContext<T>;
    return (
        <>
            <label>
                {props.label()}
            </label>
            <input
                name={props.field as string}
                type="text"
                class="form-control"
                value={model[props.field] as string}
                onInput={(e) => setModel(props.field, e.currentTarget.value as any)}
                use:validate={props.validators || []}
            />
            <ErrorMessage error={errors[props.field]} />
        </>
    )
};

interface IModelEditor<T> {
    field: keyof KeyOfType<T, string>;
    editor: keyof KeyOfType<T, IQuillEditorWrapper>
    label?: () => JSXElement;
    required?: boolean;
    error?: JSXElement;
}
export function ModelEditor<T extends object>(props: IModelEditor<T>) {
    const [model, setModel, errors, validate] = useModel() as any;
    return (
        <>
            <label>
                <Show when={props.required}><Required /></Show>
                <Show when={props.label}>
                    {props.label()}
                </Show>
                <Show when={!props.label}>
                    {props.field}
                </Show>
            </label>
            <QuillEditor content={model[props.field] as string} setInstance={(editor) => setModel(props.editor, editor) } />
            <Show when={props.required && model[props.editor]?.isDirty() && model[props.editor]?.isEmpty()}>
                <div class="error-message">
                    <Show when={props.error}>
                        {props.error}
                    </Show>
                    <Show when={!props.error}>
                        You must enter some text
                    </Show>
                </div>
            </Show>
        </>
    )
}

interface IModelImageCropper<T extends object> {
    imageUrl: () => string;
    editor: keyof KeyOfType<T, IImageCropperWrapper>
    required?: boolean;
}
export function ModelImageCropper<T extends object>(props: IModelImageCropper<T>) {
    const [model, setModel] = useModel() as any;
    return (
        <ImageCropper src={props.imageUrl()} setInstance={(wrapper) => setModel(props.editor, wrapper)} />
    );
}

interface IModelCheckbox<T extends object> { field:keyof KeyOfType<T, boolean>; label: () => JSXElement;}
export function ModelCheckbox<T extends object>(props: IModelCheckbox<T>){
    const [model, setModel] = useModel() as TModelContext<T>;
    return ( 
    <>
        <div class="checkbox">
            <input
                type="checkbox"
                checked={model[props.field] as boolean}
                onInput={() => setModel(props.field, !model[props.field])}
            />
            {props.label()}
        </div>
    </>
    )
};